import { useState, useEffect, useCallback } from "react";
import api from "../lib/adminApi";
import { useToast } from "../components/Toast";
import { Package, Plus, Pencil, Trash2, X, Check, ChevronDown, ChevronUp, Search, Loader2 } from "lucide-react";

const CURRENCY = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
const fmtLimit = (v) => (v === -1 || v === null || v === undefined) ? "∞" : String(v);

const CURRENCIES = [
  { code: 'INR', symbol: '₹', flag: '🇮🇳' },
  { code: 'AED', symbol: 'AED ', flag: '🇦🇪' },
  { code: 'SAR', symbol: 'SAR ', flag: '🇸🇦' },
  { code: 'QAR', symbol: 'QAR ', flag: '🇶🇦' },
  { code: 'OMR', symbol: 'OMR ', flag: '🇴🇲' },
  { code: 'BHD', symbol: 'BHD ', flag: '🇧🇭' },
  { code: 'KWD', symbol: 'KWD ', flag: '🇰🇼' },
  { code: 'USD', symbol: '$', flag: '🇺🇸' },
];
const HEADLINE_CURRENCIES = ['INR', 'AED', 'SAR', 'USD'];
const fmtPrice = (code, val) => {
  const c = CURRENCIES.find(x => x.code === code) || { symbol: code + ' ' };
  return `${c.symbol}${Number(val || 0).toLocaleString(code === 'INR' ? 'en-IN' : 'en-US')}`;
};
const initPrices = (prices) => {
  const out = {};
  for (const c of CURRENCIES) {
    out[c.code] = { monthly: prices?.[c.code]?.monthly ?? 0, annual: prices?.[c.code]?.annual ?? 0 };
  }
  return out;
};

export default function PackagesPage() {
  const toast = useToast();
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | pkg object
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/packages");
      setPkgs(res.data.items || []);
    } catch (e) {
      toast.error("Failed to load packages");
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (slug) => {
    try {
      await api.delete(`/packages/${slug}`);
      toast.success(`Package "${slug}" deleted`);
      setDeleting(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="packages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B0F1A]">Packages & Products</h1>
          <p className="text-sm text-[#5B617A] mt-1">{pkgs.length} plans configured</p>
        </div>
        <button onClick={() => setEditing("new")} data-testid="new-package-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#7C5CFF] text-white text-sm font-semibold hover:bg-[#6A4DE0] transition-colors">
          <Plus size={16} /> New Package
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-48 bg-white/60 rounded-2xl animate-pulse border border-[#E2E8F0]" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pkgs.map(p => (
            <PkgCard key={p.slug} pkg={p} onEdit={() => setEditing(p)} onDelete={() => setDeleting(p)} />
          ))}
        </div>
      )}

      {editing && (
        <EditModal
          pkg={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          toast={toast}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete "${deleting.name}"?`}
          message="This will soft-delete this package. Existing tenants on this plan won't be migrated automatically."
          onConfirm={() => handleDelete(deleting.slug)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

/* ── Package Card ── */
function PkgCard({ pkg, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [showAllPrices, setShowAllPrices] = useState(false);
  const p = pkg;
  return (
    <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={`pkg-card-${p.slug}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#7C5CFF]/10 flex items-center justify-center">
            <Package size={18} className="text-[#7C5CFF]" />
          </div>
          <div>
            <h3 className="font-bold text-[#0B0F1A]">{p.name}</h3>
            <p className="text-xs text-[#5B617A]">slug: {p.slug} · plan_key: {p.plan_key}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${p.active ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-red-50 text-red-500 border border-red-200"}`}>
            {p.active ? "Active" : "Inactive"}
          </span>
          {p.is_canonical && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-200">
              Canonical
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-2">
          <div className="bg-[#F8FAFC] rounded-xl p-3 flex-1">
            <p className="text-[10px] text-[#5B617A] uppercase font-bold tracking-wider mb-1">Monthly</p>
            <p className="text-[13px] font-bold text-[#0B0F1A] tabular-nums leading-tight" data-testid={`pkg-monthly-${p.slug}`}>
              {CURRENCIES.filter(c => HEADLINE_CURRENCIES.includes(c.code)).map((c, i) => (
                <span key={c.code}>{i > 0 && <span className="text-[#CBD5E1] mx-1">·</span>}{fmtPrice(c.code, p.prices?.[c.code]?.monthly)}</span>
              ))}
              <span className="text-[#CBD5E1] ml-1">/mo</span>
            </p>
          </div>
          <div className="bg-[#F8FAFC] rounded-xl p-3 shrink-0">
            <p className="text-[10px] text-[#5B617A] uppercase font-bold tracking-wider mb-1">AI Credits</p>
            <p className="text-lg font-extrabold text-[#7C5CFF] tabular-nums">{p.ai_credits_included}</p>
          </div>
        </div>
        <button onClick={() => setShowAllPrices(!showAllPrices)}
          className="text-[11px] text-[#7C5CFF] font-semibold hover:underline px-1 flex items-center gap-1"
          data-testid={`pkg-view-all-prices-${p.slug}`}>
          {showAllPrices ? 'Hide' : 'View all 8 currencies'}
          {showAllPrices ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        {showAllPrices && (
          <div className="border border-[#E2E8F0] rounded-xl overflow-hidden text-[11px]">
            <div className="grid grid-cols-[70px_1fr_1fr] bg-[#F8FAFC] text-[10px] font-bold uppercase tracking-wider text-[#5B617A]">
              <div className="px-2 py-1"></div>
              <div className="px-2 py-1 border-l border-[#E2E8F0]">Monthly</div>
              <div className="px-2 py-1 border-l border-[#E2E8F0]">Annual</div>
            </div>
            {CURRENCIES.map(c => (
              <div key={c.code} className="grid grid-cols-[70px_1fr_1fr] border-t border-[#E2E8F0]">
                <div className="px-2 py-1 font-semibold text-[#0B0F1A]">{c.flag} {c.code}</div>
                <div className="px-2 py-1 border-l border-[#E2E8F0] tabular-nums text-[#0B0F1A]">{fmtPrice(c.code, p.prices?.[c.code]?.monthly)}</div>
                <div className="px-2 py-1 border-l border-[#E2E8F0] tabular-nums text-[#94A3B8]">{fmtPrice(c.code, p.prices?.[c.code]?.annual)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Limits row */}
      {p.limits && (
        <div className="mt-3 px-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#5B617A]" data-testid={`pkg-limits-${p.slug}`}>
          <span><span className="font-semibold">Users:</span> {fmtLimit(p.limits.max_users)}</span>
          <span><span className="font-semibold">Products:</span> {fmtLimit(p.limits.max_products)}</span>
          <span><span className="font-semibold">Invoices/mo:</span> {fmtLimit(p.limits.max_invoices_per_month)}</span>
          <span><span className="font-semibold">AI/mo:</span> {fmtLimit(p.limits.max_ai_requests_per_month)}</span>
        </div>
      )}

      {/* Features toggle */}
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-[#5B617A] mt-3 hover:text-[#7C5CFF] transition-colors">
        {p.features?.length || 0} features {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {expanded && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(p.features || []).map(f => (
            <span key={f} className="px-2 py-0.5 bg-[#7C5CFF]/5 text-[#7C5CFF] text-[11px] rounded-lg font-medium border border-[#7C5CFF]/10">{f}</span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#F1F5F9]">
        <button onClick={onEdit} data-testid={`pkg-edit-${p.slug}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#5B617A] hover:bg-[#7C5CFF]/5 hover:text-[#7C5CFF] transition-colors">
          <Pencil size={12} /> Edit
        </button>
        {!p.is_canonical && (
          <button onClick={onDelete} data-testid={`pkg-delete-${p.slug}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors">
            <Trash2 size={12} /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Edit / Create Modal ── */
function EditModal({ pkg, onClose, onSaved, toast }) {
  const isNew = !pkg;
  const [form, setForm] = useState({
    slug: pkg?.slug || "",
    name: pkg?.name || "",
    ai_credits_included: pkg?.ai_credits_included ?? 0,
    features: pkg?.features || [],
    active: pkg?.active ?? true,
  });
  const [prices, setPrices] = useState(() => initPrices(pkg?.prices));
  const origPrices = pkg?.prices || {};
  const [limits, setLimits] = useState({
    max_users: pkg?.limits?.max_users ?? 0,
    max_products: pkg?.limits?.max_products ?? 0,
    max_invoices_per_month: pkg?.limits?.max_invoices_per_month ?? 0,
    max_ai_requests_per_month: pkg?.limits?.max_ai_requests_per_month ?? 0,
  });
  const origLimits = pkg?.limits || {};
  const [newFeature, setNewFeature] = useState("");
  const [saving, setSaving] = useState(false);

  // Feature catalog state
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [featureSearch, setFeatureSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const res = await api.get("/features/catalog");
        if (!cancelled) setCatalog(res.data);
      } catch {
        if (!cancelled) {
          setCatalogError(true);
          toast.error("Could not load feature catalog, falling back to manual input.");
        }
      } finally { if (!cancelled) setCatalogLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [toast]);

  // Build key→feature lookup
  const catalogMap = {};
  if (catalog) catalog.features.forEach(f => { catalogMap[f.key] = f; });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const toggleFeature = (key) => {
    set("features", form.features.includes(key)
      ? form.features.filter(x => x !== key)
      : [...form.features, key]);
  };

  const addFeature = () => {
    const f = newFeature.trim().toLowerCase().replace(/\s+/g, "_");
    if (f && !form.features.includes(f)) {
      set("features", [...form.features, f]);
      setNewFeature("");
    }
  };

  const removeFeature = (f) => set("features", form.features.filter(x => x !== f));

  // Filter catalog by search
  const q = featureSearch.toLowerCase().trim();
  const filteredByCategory = (cat) => {
    if (!catalog) return [];
    let items = catalog.features.filter(f => f.category === cat);
    if (q) items = items.filter(f =>
      f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q) || f.category.toLowerCase().includes(q)
    );
    return items;
  };
  const visibleCategories = catalog
    ? catalog.categories.filter(cat => filteredByCategory(cat).length > 0)
    : [];

  const handleSave = async () => {
    for (const c of CURRENCIES) {
      for (const cycle of ['monthly', 'annual']) {
        if (Number(prices[c.code]?.[cycle]) < 0) {
          toast.error(`${c.code} ${cycle} price cannot be negative`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/packages", {
          slug: form.slug, name: form.name,
          ai_credits_included: Number(form.ai_credits_included),
          features: form.features,
          active: form.active,
          limits: { ...limits },
          prices,
        });
        toast.success(`Package "${form.name}" created`);
      } else {
        const changedLimits = {};
        for (const k of Object.keys(limits)) {
          if (limits[k] !== (origLimits[k] ?? 0)) changedLimits[k] = limits[k];
        }
        const changedPrices = {};
        for (const c of CURRENCIES) {
          const changed = {};
          if (Number(prices[c.code]?.monthly) !== Number(origPrices[c.code]?.monthly ?? 0)) changed.monthly = Number(prices[c.code].monthly);
          if (Number(prices[c.code]?.annual) !== Number(origPrices[c.code]?.annual ?? 0)) changed.annual = Number(prices[c.code].annual);
          if (Object.keys(changed).length > 0) changedPrices[c.code] = changed;
        }
        const payload = {
          name: form.name,
          ai_credits_included: Number(form.ai_credits_included),
          features: form.features,
          active: form.active,
        };
        if (Object.keys(changedLimits).length > 0) payload.limits = changedLimits;
        if (Object.keys(changedPrices).length > 0) payload.prices = changedPrices;
        await api.patch(`/packages/${pkg.slug}`, payload);
        toast.success(`Package "${form.name}" updated`);
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x.msg || JSON.stringify(x)).join("; ") : (typeof d === "string" ? d : "Save failed");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl border border-[#E2E8F0] w-full max-w-lg max-h-[85vh] overflow-y-auto p-6"
        data-testid="pkg-edit-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-extrabold text-[#0B0F1A]">{isNew ? "New Package" : `Edit "${pkg.name}"`}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-[#5B617A]"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          {isNew && (
            <Field label="Slug (lowercase, no spaces)">
              <input value={form.slug} onChange={e => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                className="input-field" data-testid="pkg-slug-input" placeholder="e.g. enterprise" />
            </Field>
          )}

          <Field label="Display Name">
            <input value={form.name} onChange={e => set("name", e.target.value)}
              className="input-field" data-testid="pkg-name-input" />
          </Field>

          <Field label="Prices (8 currencies × 2 cycles)">
            <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
              <div className="grid grid-cols-[80px_1fr_1fr] bg-[#F8FAFC] text-[10px] font-bold uppercase tracking-wider text-[#5B617A]">
                <div className="px-3 py-2">Currency</div>
                <div className="px-3 py-2 border-l border-[#E2E8F0]">Monthly</div>
                <div className="px-3 py-2 border-l border-[#E2E8F0]">Annual</div>
              </div>
              {CURRENCIES.map(c => (
                <div key={c.code} className="grid grid-cols-[80px_1fr_1fr] border-t border-[#E2E8F0]">
                  <div className="px-3 py-2 flex items-center gap-1.5 text-xs font-semibold text-[#0B0F1A]">
                    <span>{c.flag}</span> {c.code}
                  </div>
                  <div className="px-2 py-1.5 border-l border-[#E2E8F0]">
                    <input
                      type="number" min="0" step="0.01"
                      value={prices[c.code]?.monthly ?? 0}
                      onChange={e => setPrices(prev => ({ ...prev, [c.code]: { ...prev[c.code], monthly: Number(e.target.value) || 0 } }))}
                      className="w-full px-2 py-1 rounded-lg border border-[#E2E8F0] text-sm tabular-nums focus:border-[#7C5CFF] focus:ring-1 focus:ring-[#7C5CFF]/30 outline-none"
                      data-testid={`pkg-price-${c.code}-monthly`}
                    />
                  </div>
                  <div className="px-2 py-1.5 border-l border-[#E2E8F0]">
                    <input
                      type="number" min="0" step="0.01"
                      value={prices[c.code]?.annual ?? 0}
                      onChange={e => setPrices(prev => ({ ...prev, [c.code]: { ...prev[c.code], annual: Number(e.target.value) || 0 } }))}
                      className="w-full px-2 py-1 rounded-lg border border-[#E2E8F0] text-sm tabular-nums focus:border-[#7C5CFF] focus:ring-1 focus:ring-[#7C5CFF]/30 outline-none"
                      data-testid={`pkg-price-${c.code}-annual`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Field>

          <Field label="AI Credits Included">
            <input type="number" value={form.ai_credits_included} onChange={e => set("ai_credits_included", e.target.value)}
              className="input-field tabular-nums" data-testid="pkg-credits-input" />
          </Field>

          <Field label={`Features (${form.features.length} selected)`}>
            {/* Selected features as chips */}
            {form.features.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.features.map(f => (
                  <span key={f} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#7C5CFF]/5 text-[#7C5CFF] text-xs rounded-lg border border-[#7C5CFF]/10">
                    {catalogMap[f]?.label || f}
                    <button onClick={() => removeFeature(f)} className="hover:text-red-500" data-testid={`pkg-feature-remove-${f}`}><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}

            {catalogLoading ? (
              <div className="flex items-center justify-center gap-2 py-4 text-xs text-[#5B617A]">
                <Loader2 size={14} className="animate-spin" /> Loading feature catalog...
              </div>
            ) : catalog && !catalogError ? (
              <>
                {/* Search input */}
                <div className="relative mb-2">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <input
                    value={featureSearch}
                    onChange={e => setFeatureSearch(e.target.value)}
                    className="input-field pl-8 text-sm"
                    placeholder="Search features..."
                    data-testid="pkg-feature-search"
                  />
                </div>

                {/* Catalog grouped by category */}
                <div className="max-h-52 overflow-y-auto border border-[#E2E8F0] rounded-xl" data-testid="pkg-feature-catalog">
                  {visibleCategories.length === 0 ? (
                    <p className="text-xs text-[#5B617A] text-center py-3">No features match "{featureSearch}"</p>
                  ) : visibleCategories.map(cat => (
                    <div key={cat}>
                      <div className="px-3 py-1 bg-[#F8FAFC] text-[10px] font-bold uppercase tracking-wider text-[#5B617A] border-b border-[#E2E8F0] sticky top-0">
                        {cat}
                      </div>
                      {filteredByCategory(cat).map(f => {
                        const checked = form.features.includes(f.key);
                        return (
                          <label key={f.key}
                            className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer transition-colors text-sm ${checked ? "bg-[#7C5CFF]/5" : "hover:bg-[#F8FAFC]"}`}
                            data-testid={`pkg-feature-item-${f.key}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleFeature(f.key)}
                              className="w-3.5 h-3.5 rounded border-[#CBD5E1] text-[#7C5CFF] focus:ring-[#7C5CFF] shrink-0"
                            />
                            <span className={`text-[13px] ${checked ? "font-semibold text-[#7C5CFF]" : "text-[#0B0F1A]"}`}>{f.label}</span>
                            <span className="text-[10px] text-[#94A3B8] ml-auto shrink-0">{f.key}</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              /* Fallback to manual input if catalog failed */
              <div className="flex gap-2">
                <input value={newFeature} onChange={e => setNewFeature(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addFeature())}
                  className="input-field flex-1" placeholder="Add feature key..." data-testid="pkg-feature-input" />
                <button onClick={addFeature} className="px-3 py-2 rounded-lg bg-[#F1F5F9] text-[#5B617A] text-xs font-semibold hover:bg-[#E2E8F0]">Add</button>
              </div>
            )}
          </Field>

          {/* Limits section */}
          <Field label="Limits">
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "max_users", label: "Max Users" },
                { key: "max_products", label: "Max Products" },
                { key: "max_invoices_per_month", label: "Invoices / Month" },
                { key: "max_ai_requests_per_month", label: "AI Requests / Month" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-[10px] font-semibold text-[#5B617A] mb-1">{label}</label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={-1}
                      step={1}
                      value={limits[key]}
                      onChange={e => setLimits(prev => ({ ...prev, [key]: parseInt(e.target.value, 10) || 0 }))}
                      className="input-field flex-1 tabular-nums"
                      data-testid={`pkg-limit-${key}`}
                    />
                    <button
                      type="button"
                      onClick={() => setLimits(prev => ({ ...prev, [key]: -1 }))}
                      title="Set Unlimited"
                      className={`px-2 py-2 rounded-lg text-[10px] font-bold shrink-0 transition-colors ${limits[key] === -1 ? "bg-[#7C5CFF] text-white" : "bg-[#F1F5F9] text-[#5B617A] hover:bg-[#E2E8F0]"}`}
                      data-testid={`pkg-limit-unlimited-${key}`}
                    >
                      ∞
                    </button>
                  </div>
                  {limits[key] === -1 && <span className="text-[9px] text-[#7C5CFF] font-medium">Unlimited</span>}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#5B617A] mt-2">Set <code className="bg-[#F1F5F9] px-1 rounded">-1</code> or click ∞ for unlimited.</p>
          </Field>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={e => set("active", e.target.checked)}
              className="w-4 h-4 rounded border-[#CBD5E1] text-[#7C5CFF] focus:ring-[#7C5CFF]" data-testid="pkg-active-input" />
            <span className="text-sm font-medium text-[#0B0F1A]">Active</span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-[#F1F5F9]">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-[#5B617A] hover:bg-[#F1F5F9]">Cancel</button>
          <button onClick={handleSave} disabled={saving || (!isNew && !form.name)} data-testid="pkg-save-btn"
            className="px-5 py-2 rounded-xl bg-[#7C5CFF] text-white text-sm font-semibold hover:bg-[#6A4DE0] transition-colors disabled:opacity-50">
            {saving ? "Saving..." : isNew ? "Create" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full border border-[#E2E8F0]" data-testid="confirm-dialog">
        <h3 className="text-lg font-bold text-[#0B0F1A] mb-2">{title}</h3>
        <p className="text-sm text-[#5B617A] mb-5">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 rounded-xl text-sm font-semibold text-[#5B617A] hover:bg-[#F1F5F9]">Cancel</button>
          <button onClick={onConfirm} data-testid="confirm-yes-btn"
            className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600">Confirm</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[#5B617A] uppercase tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );
}
